import { PropsWithChildren, ReactElement, useMemo } from "react";
import { useFormApi } from "@avinet/adaptive-ui-core/form/useForm";
import LabeledControl from "@avinet/adaptive-ui-core/form/utils/LabeledControl";
import Grow from "@avinet/adaptive-ui-core/layout/Grow";
import Row from "@avinet/adaptive-ui-core/layout/Row";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import FormBaseProps from "../../../types/FormBaseProps";
import Section, {
  GeometrySectionType,
  InputSectionType,
  RatingInputSection,
  SectionType,
} from "../../../types/Section";
import { formatDate } from "../../../utils/date";
import { getRatingLabel } from "../../results/ResultsTable";

interface DisplayControlProps extends FormBaseProps {
  /**
   * The section type.
   */
  type: SectionType;
  /**
   * Is the field required.
   */
  required?: boolean;

  /**
   * If specified, used to render section specific values.
   */
  section?: Section;
}

function DisplayControl({
  section,
  id,
  className,
  langKey,
  label,
  type,
  required = false,
  children,
}: PropsWithChildren<DisplayControlProps>): ReactElement {
  const form = useFormApi();
  const t = useTranslate(form.langSet);
  const { getValue } = form;
  const value = getValue(id);

  const displayValue = useMemo(() => {
    switch (type) {
      case InputSectionType.media:
        return !!value ? (
          <img src={typeof value === "string" ? value : value.dataUrl} alt="" />
        ) : (
          t("unanswered")
        );

      case InputSectionType.boolean:
        return !!value ? t("yes") : t("no");

      case InputSectionType.date:
        return !!value ? formatDate(value) : t("unanswered");

      case InputSectionType.multiselect_list:
        return !!value && value.length > 0 ? value.join(", ") : t("unanswered");

      case InputSectionType.rating:
        const ratingSection = section as RatingInputSection;
        return value === undefined || value === null
          ? t("unanswered")
          : !!ratingSection
          ? getRatingLabel(ratingSection.symbol, value, ratingSection.max ?? 5)
          : value;

      case GeometrySectionType.geometry_point:
      case GeometrySectionType.geometry_line:
      case GeometrySectionType.geometry_polygon:
        return !!value ? t("answered") : t("unanswered");

      default:
        return value === undefined || value === null || value === ""
          ? t("unanswered")
          : value;
    }
  }, [t, type, value, section]);

  /* TODO Format value according to type */
  return (
    <LabeledControl
      className={`display-control ${className ?? ""}`}
      id={id}
      langKey={langKey}
      label={label}
      required={required && type !== "boolean"}
    >
      <Row>
        <span className="display-value">{displayValue}</span>
        <Grow />
        {children}
      </Row>
    </LabeledControl>
  );
}

export default DisplayControl;
